// frontend/src/modules/projects/components/CreateTaskDialog.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { projectsApi } from '../api';
import type { Task } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
  employees: EmployeeWithUser[];
  onCreated: (task: Task) => void;
}

export function CreateTaskDialog({ open, onOpenChange, projectId, employees, onCreated }: Props) {
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [assigneeId, setAssigneeId] = React.useState('');
  const [dueDate, setDueDate] = React.useState('');
  const [isPrivate, setIsPrivate] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) { setTitle(''); setDescription(''); setAssigneeId(''); setDueDate(''); setIsPrivate(false); }
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      const t = await projectsApi.createTask({
        project_id: projectId,
        title: title.trim(),
        description: description || null,
        assignee_employee_id: assigneeId || null,
        due_date: dueDate || null,
        is_private: isPrivate,
      });
      onCreated(t);
    } catch (err: any) {
      toast.error('Create failed', { description: err.message });
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label>Title<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="grid gap-1.5">
            <Label>Description</Label>
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-1.5">
              <Label>Assignee</Label>
              <Select value={assigneeId} onValueChange={setAssigneeId}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2.5 text-[13px] text-foreground cursor-pointer select-none">
            <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-saffron" />
            Private (visible only to the assignee + managers)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create task'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
